import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { site } from '@shared/site.config';

export default function OwnerBriefLeadForm() {
  const { pathname, search } = useLocation();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    ownerType: 'Commercial property owner',
    city: 'Cambridge',
    timing: 'Just keeping informed',
    notes: '',
  });
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  const set =
    (key: keyof typeof form) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    setError('');

    const message = [
      'KWC / Guelph Commercial Owner Brief enquiry',
      `Owner type: ${form.ownerType}`,
      `City: ${form.city}`,
      `Timing: ${form.timing}`,
      form.notes.trim() ? `Notes: ${form.notes.trim()}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    try {
      const result = await api.submitLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        company: form.company.trim() || undefined,
        message,
        interest: 'buying-selling',
        sourcePath: pathname + search,
      });
      if (!result.id) throw new Error('Your inquiry was not stored. Please try again.');
      setState('sent');
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  if (state === 'sent') {
    return (
      <div className="sheet-panel sheet-marked shadow-sheet p-8 text-center">
        <p className="annot">Owner brief request received</p>
        <h3 className="mt-2 font-display text-[26px]">You’re on the owner-interest list.</h3>
        <p className="mt-3 text-[14px] leading-relaxed text-annot">
          Your details went directly to {site.agent.shortName}. This is not an automated valuation or legal opinion; it simply tells us what type of local commercial information matters to you.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="sheet-panel shadow-sheet p-6 sm:p-7">
      <div className="mb-6 border-b border-hairline pb-5">
        <p className="annot">Owner interest</p>
        <h3 className="mt-2 font-display text-[26px] leading-tight">Get the next KWC / Guelph Owner Brief</h3>
        <p className="mt-2 text-[14px] leading-relaxed text-annot">
          Tell us what you own or operate so future briefs can focus on the issues local owners actually care about.
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="annot">Name</span>
            <input required value={form.name} onChange={set('name')} className="field mt-1.5" autoComplete="name" />
          </label>
          <label className="block">
            <span className="annot">Email</span>
            <input required type="email" value={form.email} onChange={set('email')} className="field mt-1.5" autoComplete="email" />
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="annot">Phone — optional</span>
            <input value={form.phone} onChange={set('phone')} className="field mt-1.5" autoComplete="tel" />
          </label>
          <label className="block">
            <span className="annot">Company — optional</span>
            <input value={form.company} onChange={set('company')} className="field mt-1.5" autoComplete="organization" />
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="annot">I am a…</span>
            <select value={form.ownerType} onChange={set('ownerType')} className="field mt-1.5">
              <option>Commercial property owner</option>
              <option>Business owner</option>
              <option>Landlord</option>
              <option>Investor / buyer</option>
              <option>Tenant / occupier</option>
              <option>Professional advisor</option>
            </select>
          </label>
          <label className="block">
            <span className="annot">Primary city</span>
            <select value={form.city} onChange={set('city')} className="field mt-1.5">
              <option>Cambridge</option>
              <option>Kitchener</option>
              <option>Waterloo</option>
              <option>Guelph</option>
              <option>Other Waterloo Region / Wellington</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="annot">Current timing</span>
          <select value={form.timing} onChange={set('timing')} className="field mt-1.5">
            <option>Just keeping informed</option>
            <option>Considering a sale in 6–12 months</option>
            <option>Considering a sale in 3–6 months</option>
            <option>Considering a sale within 90 days</option>
            <option>Reviewing a lease / renewal</option>
            <option>Looking to buy or expand</option>
          </select>
        </label>

        <label className="block">
          <span className="annot">What should we cover? — optional</span>
          <textarea
            rows={3}
            value={form.notes}
            onChange={set('notes')}
            className="field mt-1.5 resize-y"
            placeholder="Zoning, lease rates, financing, property value, business sale, redevelopment, tenant issues…"
          />
        </label>
      </div>

      {state === 'error' && (
        <div className="mt-4 border-l-2 border-redline bg-redline-100/50 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">Not sent</p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-graphite-600">{error} You can also call {site.agent.phone} directly.</p>
        </div>
      )}

      <button type="submit" disabled={state === 'sending'} className="btn-primary mt-6 w-full">
        {state === 'sending' ? 'Sending…' : 'Request the next Owner Brief'}
      </button>
      <p className="mt-4 text-center text-[12px] leading-relaxed text-annot">
        Goes directly to {site.agent.name}. No third-party lead resale.
      </p>
    </form>
  );
}
